import { Optional } from '@ag-grid-community/core';
import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';

@GridModalComponent('matcert')
@Component({
    selector: 'tt-matcert',
    templateUrl: './matcert.component.html',
    styleUrls: ['./matcert.component.css'],
})
export class MatcertComponent implements OnChanges {
    @Input()
    public params?: MatcertParams;

    public get?: MatcertGet;

    public grid?: GridOptions;

    public testsGrid?: GridOptions;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public deleting = false;

    public saving = false;

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) stateContext: StateDialogContext,
    ) {
        this.initFromDialog(stateContext);
    }

    public onTabChanged() {
        if (this.get?.gb_matcert_main === 'TESTS') {
            this.readTestsGrid();
        } else if (this.get?.gb_matcert_main === 'PRODUCTS') {
            // this.readGrid();
        }
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const { errorcode, errormessage } = await this.saveMatcert(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async deleteMatcert() {
        if (!this.get) return;

        try {
            this.deleting = true;

            const { errorcode, errormessage } = await this._deleteMatcert(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getMatcert(this.params);
        }
    }

    private readGrid() {
        if (!this.grid) {
            this.grid = new Grid().setRememberId('w_matcert_grid').setLoadDataFunction({ method: 3936, parameters: () => this.get });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private readTestsGrid() {
        if (!this.testsGrid) {
            this.testsGrid = new Grid()
                .setRememberId('w_matcert_tests_grid')
                .setLoadDataFunction({ method: 3940, parameters: () => this.get })
                .setSpecialFunc({
                    buttons: [
                        {
                            name: 'open_chargetest',
                            icon: 'fas fa-pen',
                            btn_type: 'primary',
                            tooltip: 'open_chargetest',
                            translateTooltip: true,
                            func: async (row: GridRow) => {
                                const response = await this.modal.openStateDialog({ state: 'chargetest', params: row['charge_keyno'] + '/' + row['chargetest_keyno'] });

                                if (!!response) {
                                    this.testsGrid?.gridfunc?.read();
                                }
                            },
                        },
                    ],
                });
        } else {
            this.testsGrid.gridfunc?.read();
        }
    }

    private async getMatcert(params: MatcertParams): Promise<MatcertGet> {
        return (await this.datatask.Post(3935, params))[0];
    }

    private async saveMatcert(params: MatcertGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3941, params))[0];
    }

    private async _deleteMatcert(params: MatcertGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3938, params))[0];
    }

    private async init() {
        await this.loadGet();
        this.readGrid();
        this.readTestsGrid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state) {
            state.setSize({ width: 'min(200rem, 95vw)', height: '95vh' });
            this.params = state.params as unknown as MatcertParams;
            await this.init();
            state.setHeading(this.get?.webpage_heading ?? '');
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => state.close(value) });
            //
        }
    }
}

export interface MatcertParams {
    matcert_keyno: string;
}

interface MatcertGet {
    matcert_keyno: string;
    matcert_no: string | number;
    certificate_date: string;
    order_internal_no: string;
    order_offer_no: string;
    order_order_no: string;
    stat_cust_no: string;
    customer_name_and_address: string;
    webpage_heading: string;
    gb_matcert_main: string;
    serialnos: string;
    matcert_note1: string;
    matcert_note2: string;
    reg_datetime: string;
    reg_by_portal_user_name: string;
    mod_datetime: string;
    mod_by_portal_user_name: string;
}
